import { BUTTON_SIZE, BUTTON_TYPE, DSButton } from '@repo/design-system/index'
import { QIcon_arrow_left, QIcon_hint_black, QIcon_underline_black } from '@repo/icons/index'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import ReactHtmlParser from 'react-html-parser'
import { sendGTMEvent } from '../../../../../../../helpers/gtmHelper'
import {
  AccuracyMetricsDetailItem,
  COMPLEXITY_METRIC,
  ComplexityDetailItem,
  HIGHLIGHT_METRIC,
} from '../../../../../../../model/caf.interface'
import { useCafMetricsDetails } from '../../../../../../../modules/apis/webview/caf'
import useAppRouter from '../../../../../../../modules/hooks/common/useAppRouter'
import useHtmlParserTransform from '../../../../../../../modules/hooks/webview/caf/common/useHtmlParserTransform'
import { mapAccuracyEnum } from '../../../../../../../modules/hooks/webview/caf/common/useMapAccuracyEnum'
import { s_caf } from '../../../../../../../modules/i18n/strings/auto/wording_webview'
import useTranslation from '../../../../../../../modules/i18n/useTranslation'
import { STUDENT_BTN_ID } from '../../../../../../../modules/ids/gtm/student'
import {
  ICON_ALARM_GRAY,
  ICON_CHECK_CIRCLE_GREEN_2,
  ICON_CHEVRON_DOWN_GRAY700,
  ICON_CHEVRON_UP_GRAY700,
} from '../../../../../../../modules/svg/svg'
import urls from '../../../../../../../modules/urls'
import { COLORS } from '../../../../../../../modules/vars'
import LoadingSpinner from '../../../../../../common/LoadingSpinner'
import { ComplexityDetailSection } from '../../../../../../webview/caf/analysis/complexity/ComplexityDetailSection'
import Accordion from '../../../../../../webview/caf/common/Accordion'
import AudioController from '../../../../../../webview/caf/common/AudioController'
import { useCafSidePopup } from '../../../../../../webview/caf/common/CafSidePopup'
import AccordionContainer from '../../../../../../webview/caf/highlight/AccordionContainer'
import AccordionTitle from '../../../../../../webview/caf/highlight/AccordionTitle'
import CafErrorReportPopup from './CafErrorReportPopup'
import SidePopupBody from './components/Body'

export interface SidePopupBodyProps {
  headerProps: SidePopupHeaderProps
  children: JSX.Element | JSX.Element[] | string | string[]
}
export interface SidePopupHeaderProps {
  title: string | JSX.Element
  onClose: () => void
  SubIcon?: any
  onClickSubIcon?: any
}
type AnalysisType = 'Complexity' | 'Accuracy' | 'Fluency' | null

const DEFAULT_PROPS = {
  hFull: true,
  bgWhite: true,
  maxW: 512,
  noTopSide: true,
  overlayClose: true,
}
const useMetricDetailSidePopup = (useBackIcon = false) => {
  const router = useAppRouter()
  const sidePopup = useCafSidePopup()
  const { t } = useTranslation()
  const { lessonId, tab, componentId, metricTag, back, title, metricId, errorTag } = router.query

  const analysisType = +tab === 1 ? 'Complexity' : +tab === 2 ? 'Accuracy' : 'Fluency'
  const popupId = `detail_${metricId}_${errorTag}_${analysisType}`
  const goBack = () => {
    router.push(
      urls.student.portal.lessons.reviewDetail.asCafMetric(lessonId, tab, componentId, metricTag) + '&back=true'
    )
  }

  useEffect(() => {
    const shouldOpenComplexityPopup = analysisType === 'Complexity' && +metricId >= 0 && +errorTag >= 0
    const shouldOpenAccuracyPopup = analysisType === 'Accuracy' && +metricId >= 0 && +errorTag >= 0

    if (shouldOpenComplexityPopup || shouldOpenAccuracyPopup) {
      sidePopup.show({
        ...DEFAULT_PROPS,
        id: popupId,
        body: (
          <SidePopupBody
            headerProps={{
              title: (
                <div className="flex flex-row items-center justify-start text-[20px] font-bold text-black">
                  {useBackIcon && (
                    <QIcon_arrow_left width={24} height={24} className="mr-[8px] cursor-pointer" onClick={goBack} />
                  )}
                  {title}
                </div>
              ),
              onClose: goBack,
            }}
          >
            <div className="flex min-h-screen w-full flex-col items-start justify-start pr-[48px]">
              <Content metricId={+metricId} errorTag={+errorTag} analysisType={analysisType} />
            </div>
          </SidePopupBody>
        ),
        bgColor: COLORS.WHITE,
        noScrollbar: false,
      })
    }
  }, [metricId, errorTag, analysisType])
}

interface ContentProps {
  metricId: number
  errorTag: number
  analysisType: 'Complexity' | 'Accuracy' | 'Fluency'
}
const Content = ({ metricId, errorTag, analysisType }: ContentProps) => {
  const {
    data: response,
    error,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
  } = useCafMetricsDetails<ComplexityDetailItem | AccuracyMetricsDetailItem | any>(metricId, errorTag)
  const [sentenceToReport, setSentenceToReport] = useState('')
  const [showPopup, setShowPopup] = useState(false)
  const [data, setData] = useState<ComplexityDetailItem | AccuracyMetricsDetailItem | any>(null)
  const { t } = useTranslation()
  const errRef = useRef<HTMLDivElement>(null)
  const [reportId, setReportId] = useState(0)
  const [errIndex, setErrIndex] = useState(1)
  const { transformHTMLNode } = useHtmlParserTransform()
  const handleIndex = (len) => () => {
    setErrIndex((prev) => (prev > 1 ? 1 : len))
  }
  const onPlay = () => {
    sendGTMEvent(STUDENT_BTN_ID.CLICK_CAF_LESSON_HIGHLIGHT_AUDIO, {
      caf_highlight_tag: errorTag,
    })
  }
  const handleClickErrorReport = (hilightId, content) => () => {
    setReportId(hilightId)
    setSentenceToReport(content)
    setShowPopup(true)
  }

  useEffect(() => {
    if (analysisType === 'Complexity') {
      if (response?.pages[0]?.words) {
        setData({
          ...response?.pages[0],
          words:
            response?.pages?.reduce((ac: any, cr) => {
              return { ...ac, ...cr.words }
            }, []) || {},
        })
      } else if (response?.pages[0]?.sentences) {
        setData({
          ...response?.pages[0],
          sentences:
            response?.pages?.reduce((ac, cr) => {
              return [...ac, ...cr.sentences]
            }, []) || [],
        })
      } else {
        setData({
          ...response?.pages[0],
        })
      }
    } else if (analysisType === 'Accuracy') {
      setData({
        ...response?.pages[0],
        details:
          response?.pages?.reduce((ac, cr) => {
            return [...ac, ...cr.details]
          }, []) || [],
      })
    }
  }, [response, analysisType])

  if (error) return <div className="w-full break-all bg-gray-50 px-[12px]"></div>
  return (
    <div className="w-full">
      {analysisType == 'Complexity' && (
        <div className="h-full">
          <ComplexityDetailSection data={data} index={COMPLEXITY_METRIC[data?.level_type] ? 0 : 1} />
          <div className="pt-12">
            {response && !!response?.pages[response?.pages.length - 1]?.page?.next && (
              <div className="flex justify-center pb-[88px]">
                <DSButton
                  type={BUTTON_TYPE.OUTLINE}
                  key={`view-more`}
                  size={BUTTON_SIZE.SM}
                  onClick={() => fetchNextPage()}
                  customBorderColor={COLORS.PURPLE500}
                  className={classNames('mr-[8px] !w-auto !cursor-pointer !rounded-[8px]')}
                >
                  <DSButton.Label className={classNames('whitespace-nowrap !text-purple-500')}>
                    {isFetchingNextPage ? <LoadingSpinner primary show md /> : t(s_caf.complexity.viewmore_btn)}
                  </DSButton.Label>
                </DSButton>
              </div>
            )}
          </div>
        </div>
      )}

      {analysisType === 'Accuracy' && (
        <div className="keep-all min-h-screen break-words bg-white pb-[32px] text-gray-700">
          <h2 className="text-sBody mb-[16px]">
            {t(s_caf.accuracy.subsection_info(data?.count || 0, mapAccuracyEnum(data?.error_tag, t)))}
          </h2>
          <AccordionContainer>
            {data?.details?.map((errorItem, idx) => (
              <Accordion
                key={idx + 'grammar_errorItem'}
                title={<AccordionTitle type={HIGHLIGHT_METRIC.GRAMMAR} content={errorItem} index={idx + 1} tag={''} />}
                className="mb-[12px] cursor-pointer shadow-sm"
                errHeight={errRef.current?.style.height}
                idx={idx}
              >
                <article className="mb-[8px] cursor-default text-gray-700">
                  <div className="flex">
                    <img src={ICON_CHECK_CIRCLE_GREEN_2} alt="check" className="h-20 w-20" />
                    <span className="text-medium ml-[10px] text-[14px] font-medium">
                      {ReactHtmlParser(errorItem.correct_sentence, { transform: transformHTMLNode })}
                    </span>
                  </div>
                  <p className="text-sBody my-[16px] rounded bg-gray-50 p-[8px] text-[14px] ">
                    <div ref={errRef}>
                      {errorItem.errors?.slice(0, errIndex).map((item, idx) => {
                        return (
                          <div className="mb-8" key={idx}>
                            <div className="mb-8 flex items-center">
                              <QIcon_hint_black className="mr-[8px]" />
                              <span className="text-h5 mb-[4px] h-12 gap-[4px] font-medium text-black">
                                {item.title}
                              </span>
                            </div>
                            <span key={idx} className="text-sBody mb-4 block tracking-tight">
                              {item.body}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                    {errorItem.errors?.length > 1 && errIndex > 1 && (
                      <div className="mb-[16px] ml-[8px] flex-row">
                        <QIcon_underline_black width={24} height={24} className="inline" />
                        <span className="text-sm text-gray-600">{t(s_caf.accuracy.underline_text)}</span>
                      </div>
                    )}
                    {errorItem.errors?.length > 1 && (
                      <div className="flex items-center">
                        {errIndex === 1 ? (
                          <>
                            <div className="mr-4" onClick={handleIndex(errorItem.errors?.length)}>
                              {t(s_caf.accuracy.other_errors(errorItem.errors?.length - 1))}
                            </div>
                            <img src={ICON_CHEVRON_DOWN_GRAY700} width={10} height={5} alt="down" className="mb-4" />
                          </>
                        ) : (
                          <>
                            <div className="mr-4" onClick={handleIndex(errorItem.errors?.length)}>
                              {t(s_caf.accuracy.collapse)}
                            </div>
                            <img src={ICON_CHEVRON_UP_GRAY700} width={12} height={5} alt="up" className="mb-4" />
                          </>
                        )}
                      </div>
                    )}
                  </p>
                  {errorItem.audio_url && <AudioController src={errorItem.audio_url} id={idx} onPlay={onPlay} />}
                </article>
                <div
                  className="flex cursor-pointer items-center text-[12px] font-medium tracking-wide text-gray-500 sm:text-[14px]"
                  onClick={handleClickErrorReport(errorItem.id, errorItem?.sentence || errorItem?.my_sentence)}
                >
                  <img src={ICON_ALARM_GRAY} alt="alarm" className="mb-4" />
                  <div className="ml-2 mt-1">{t(s_caf.highlight.report)}</div>
                </div>
              </Accordion>
            ))}
          </AccordionContainer>
          <div className="flex w-full flex-row items-center justify-center pt-12">
            {response && !!response?.pages[response?.pages.length - 1]?.page?.next && (
              <DSButton
                type={BUTTON_TYPE.OUTLINE}
                key={`view-more`}
                size={BUTTON_SIZE.SM}
                onClick={() => fetchNextPage()}
                customBorderColor={COLORS.PURPLE500}
                className={classNames('mr-[8px] !w-auto !cursor-pointer !rounded-[8px]')}
              >
                <DSButton.Label className={classNames('whitespace-nowrap !text-purple-500')}>
                  {isFetchingNextPage ? <LoadingSpinner primary show md /> : t(s_caf.complexity.viewmore_btn)}
                </DSButton.Label>
              </DSButton>
            )}
          </div>
          <CafErrorReportPopup
            content={sentenceToReport}
            type="metric"
            isShowEditPopup={showPopup}
            setShowEditPopup={setShowPopup}
            id={reportId}
          />
        </div>
      )}
    </div>
  )
}

export default useMetricDetailSidePopup
